import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Box, Typography } from "@mui/material";

import routes from "../../../constants/routes";
import { getMyJoinRequestsAPI } from "../../../api/joinRequestsAPI";
import usePendingJoinRequests from "../../../hooks/usePendingJoinRequests";
import {
  JoinRequestChip,
  joinRequestState,
  requestsTabPath,
} from "../../commonComponents/JoinRequestStatus";

const cardSx = {
  backgroundColor: "#FFFFFF",
  border: "2px solid #2D55EB",
  p: 2.5,
  my: 2,
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
};

const link = (onClick, children) => (
  <a
    href="#/"
    onClick={(event) => {
      event.preventDefault();
      onClick();
    }}
    style={{ fontWeight: 500 }}
  >
    {children}
  </a>
);

// how long a request has waited, e.g. "today", "2 days"
export const waitedFor = (createdAt) => {
  const days = moment().diff(moment(createdAt), "days");
  if (days < 1) return "today";
  return days === 1 ? "1 day" : `${days} days`;
};

// Bidder's Dashboard: his requests to participate at a glance. Shown only
// when he has some.
export const MyRequestsCard = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    getMyJoinRequestsAPI()
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]));
  }, []);

  if (rows.length === 0) return null;

  // count by how each request stands now
  const groups = {};
  rows.forEach((row) => {
    const state = joinRequestState(row, row.isParticipant);
    (groups[state] = groups[state] || []).push(row);
  });
  const lines = ["APPROVED", "ADDED", "WAITING", "NOT_ACCEPTED"]
    .filter((state) => groups[state])
    .map((state) => {
      const first = groups[state][0];
      const needsTerms =
        (state === "APPROVED" || state === "ADDED") &&
        groups[state].some((row) => !row.acceptedTerms);
      return {
        state,
        count: groups[state].length,
        text:
          groups[state].length === 1
            ? `${first.auctionTitle}${needsTerms ? " – accept terms" : ""}`
            : `${groups[state].length} auctions${needsTerms ? " – accept terms" : ""}`,
        eventId: groups[state].length === 1 ? first.eventId : null,
      };
    });

  return (
    <Box sx={cardSx}>
      <Typography sx={{ fontSize: 18, fontWeight: 700 }}>My requests</Typography>
      {lines.map((line) => (
        <Box
          key={line.state}
          sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}
        >
          <JoinRequestChip state={line.state} />
          {line.eventId
            ? link(() => navigate(`/auctions/${line.eventId}`), line.text)
            : <span>{line.text}</span>}
        </Box>
      ))}
      {link(() => navigate(routes.myRequests), "See all my requests →")}
    </Box>
  );
};

// Organiser's (and admin's) Dashboard: requests to participate waiting for a
// decision, the newest three with a link each. Shown only when there are some.
export const JoinRequestsWaitingCard = ({ isAdmin }) => {
  const navigate = useNavigate();
  const { total, latest } = usePendingJoinRequests();
  if (!total) return null;

  return (
    <Box sx={{ ...cardSx, borderColor: "#E6B800" }}>
      <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
        Join requests waiting: {total}
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 480 }}>
          <tbody>
            {latest.map((request) => (
              <Box component="tr" key={request.eventJoinRequestId} sx={{ borderTop: "1px solid #EEF0F3" }}>
                <Box component="td" sx={{ py: 1, fontWeight: 500 }}>{request.bidderName}</Box>
                <Box component="td" sx={{ p: 1 }}>{request.auctionTitle}</Box>
                {isAdmin && <Box component="td" sx={{ p: 1 }}>{request.organiserName}</Box>}
                <Box component="td" sx={{ p: 1, color: "#4B5563" }}>{waitedFor(request.createdAt)}</Box>
                <Box component="td" sx={{ py: 1, textAlign: "right" }}>
                  {link(() => navigate(requestsTabPath(request.eventId)), "Review")}
                </Box>
              </Box>
            ))}
          </tbody>
        </Box>
      </Box>
      {isAdmin && link(() => navigate(routes.joinRequests), "See all join requests →")}
    </Box>
  );
};
